import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';

import { toastrError, toastrSuccess } from '@portals/redux/actions/toastr';

import { GROUPS_API_URL, groupsQueryKeys } from './groups.constants';
import { GroupsDetailsType, GroupType } from './groups.types';
import { useApiQuery } from '../../hooks';
import { ServerError } from '../../types';
import { uiGlobalQueryKeys } from '../../ui/global-query-keys';
import { fetchApiRequest, useRequestOptions } from '../../utils';

export function useGroups() {
  return useApiQuery<GroupType[]>(GROUPS_API_URL, groupsQueryKeys.all(), {
    staleTime: 0,
  });
}

export function useGroup(groupId: string) {
  return useApiQuery<GroupsDetailsType>(
    `${GROUPS_API_URL}/${groupId}`,
    groupsQueryKeys.detail(groupId),
    { staleTime: 0, enabled: !!groupId }
  );
}

interface UseCreateGroupMutationParams {
  name: string;
  iconName: string;
  userIds: string[];
}

export function useCreateGroup() {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const { url, options } = useRequestOptions({
    url: GROUPS_API_URL,
    method: 'POST',
  });

  return useMutation<
    GroupsDetailsType,
    ServerError,
    UseCreateGroupMutationParams
  >({
    mutationFn: ({ name, iconName, userIds }) => {
      return fetchApiRequest(url, {
        ...options,
        body: JSON.stringify({
          name,
          icon_name: iconName,
          user_ids: userIds,
        }),
      });
    },
    onSuccess: () => {
      dispatch(toastrSuccess('Group created successfully'));

      queryClient.invalidateQueries(groupsQueryKeys.all());
      queryClient.invalidateQueries(uiGlobalQueryKeys.users);
    },
    onError: ({ error }) => {
      dispatch(toastrError(error));
    },
    meta: {
      mutationName: 'useCreateGroup',
      baseUrl: GROUPS_API_URL,
      method: 'POST',
    },
  });
}

interface UseUpdateGroupParams {
  groupId: string;
  name: string;
  iconName: string;
}
export function useUpdateGroup() {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const { url, options } = useRequestOptions({
    url: GROUPS_API_URL,
    method: 'PUT',
  });

  return useMutation<any, ServerError, UseUpdateGroupParams>({
    mutationFn: ({ groupId, name, iconName }) => {
      return fetchApiRequest(`${url}/${groupId}`, {
        ...options,
        body: JSON.stringify({ name, icon_name: iconName }),
      });
    },
    onSuccess: () => {
      dispatch(toastrSuccess('Group updated successfully'));

      queryClient.invalidateQueries(groupsQueryKeys.all());
      queryClient.invalidateQueries(uiGlobalQueryKeys.users);
    },
    onError: ({ error }) => {
      dispatch(toastrError(error));
    },
    meta: {
      mutationName: 'useUpdateGroup',
      baseUrl: `${GROUPS_API_URL}/:id`,
      method: 'PUT',
    },
  });
}

export function useAddInternalUsers() {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const { url, options } = useRequestOptions({
    url: GROUPS_API_URL,
    method: 'POST',
  });

  return useMutation<any, ServerError, { groupId: string; userIds: string[] }>({
    mutationFn: ({ groupId, userIds }) => {
      return fetchApiRequest(`${url}/${groupId}/add_users`, {
        ...options,
        body: JSON.stringify({ user_ids: userIds }),
      });
    },
    onSuccess: () => {
      dispatch(toastrSuccess('Internal users added to group successfully'));

      queryClient.invalidateQueries(groupsQueryKeys.all());
      queryClient.invalidateQueries(uiGlobalQueryKeys.users);
    },
    onError: ({ error }) => {
      dispatch(toastrError(error));
    },
    meta: {
      mutationName: 'useAddInternalUsers',
      baseUrl: `${GROUPS_API_URL}/:id/add_users`,
      method: 'POST',
    },
  });
}

export function useAddExternalUser() {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const { url, options } = useRequestOptions({
    url: GROUPS_API_URL,
    method: 'POST',
  });

  return useMutation<any, ServerError, { groupId: string; email: string }>({
    mutationFn: ({ groupId, email }) => {
      return fetchApiRequest(`${url}/${groupId}/add_external_user`, {
        ...options,
        body: JSON.stringify({ email }),
      });
    },
    onSuccess: () => {
      dispatch(toastrSuccess('External user added to group successfully'));

      queryClient.invalidateQueries(groupsQueryKeys.all());
    },
    onError: ({ error }) => {
      dispatch(toastrError(error));
    },
    meta: {
      mutationName: 'useAddExternalUser',
      baseUrl: `${GROUPS_API_URL}/:id/add_external_user`,
      method: 'POST',
    },
  });
}

interface UseRemoveGroupUsersMutationParams {
  groupId: string;
  userIds: string[];
}
export function useRemoveGroupUsers() {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const { url, options } = useRequestOptions({
    url: GROUPS_API_URL,
    method: 'POST',
  });

  return useMutation<void, ServerError, UseRemoveGroupUsersMutationParams>({
    mutationFn: ({ groupId, userIds }) => {
      return fetchApiRequest(`${url}/${groupId}/remove_users`, {
        ...options,
        body: JSON.stringify({ user_ids: userIds }),
      });
    },
    onSuccess: () => {
      dispatch(toastrSuccess('Users removed successfully'));

      queryClient.invalidateQueries(groupsQueryKeys.all());
      queryClient.invalidateQueries(uiGlobalQueryKeys.users);
    },
    onError: ({ error }) => {
      dispatch(toastrError(error));
    },
    meta: {
      mutationName: 'useRemoveGroupUsers',
      baseUrl: `${GROUPS_API_URL}/:id/remove_users`,
      method: 'POST',
    },
  });
}

interface UseRemoveGroupMutationParams {
  groupId: string;
}

export function useRemoveGroup() {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const { url, options } = useRequestOptions({
    url: GROUPS_API_URL,
    method: 'DELETE',
  });

  return useMutation<void, ServerError, UseRemoveGroupMutationParams>({
    mutationFn: ({ groupId }) => {
      return fetchApiRequest(`${url}/${groupId}`, options);
    },
    onSuccess: () => {
      dispatch(toastrSuccess('Group removed successfully'));

      queryClient.invalidateQueries(groupsQueryKeys.all());
      queryClient.invalidateQueries(uiGlobalQueryKeys.users);
    },
    onError: ({ error }) => {
      dispatch(toastrError(error));
    },
    meta: {
      mutationName: 'useRemoveGroup',
      baseUrl: `${GROUPS_API_URL}/:id`,
      method: 'DELETE',
    },
  });
}
